/**
 * Pure logic for the "add exercise" form in the program editor.
 *
 * Kept out of the component so it can be tested directly, and so the client-side
 * rules live in one place rather than being implied by input types.
 *
 * These rules deliberately mirror models.Prescription.Validate in the Go backend
 * (see docs/specs/prescription-domain.md). The server remains the authority —
 * every error it returns is surfaced verbatim — but validating here too means a
 * coach editing a tempo or series gets told immediately instead of watching a
 * silent 400. Before this, an invalid prescription produced a console.error and
 * a modal that sat there looking functional.
 */

// Mirrors seriesPattern in models/prescription.go: a superset group letter with
// up to two optional digits. "A" is standalone, "A1"/"A2" are a paired superset.
import { validateRepScheme } from './repScheme';

export const SERIES_PATTERN = /^[A-Z][0-9]{0,2}$/;

// Mirrors tempoPattern: four hyphen-separated phases,
// eccentric-pause-concentric-pause, each a seconds count or "X" for explosive.
export const TEMPO_PATTERN = /^([0-9]{1,2}|X)-([0-9]{1,2}|X)-([0-9]{1,2}|X)-([0-9]{1,2}|X)$/;

// Mirrors the constants block in models/prescription.go.
//
// `load` is absent on purpose. Its bounds are the only ones here that depend on
// the user's unit, and they come from GET /api/preferences already expressed in
// it. Hardcoding -500/1000 checked a pound value against kilogram limits — the
// same defect the backend had. See units.js.
export const LIMITS = {
  sets: { min: 1, max: 20 },
  reps: { min: 1, max: 100 },
  rest: { min: 0, max: 900 },
  percentOneRM: { min: 1, max: 150 },
  rpe: { min: 1, max: 10 },
  rir: { min: 0, max: 10 },
};

// Applies until /preferences answers. Kilograms, matching DEFAULT_UNIT.
export const DEFAULT_LOAD_LIMITS = { min: -500, max: 1000 };

export const EMPTY_FORM = {
  series: 'A1',
  sets: '3',
  reps: '10',
  tempo: '4-0-1-0',
  rest: '60',
  notes: '',
  load: '',
  percentOneRM: '',
  rpe: '',
  rir: '',
};

const asText = (value) => String(value ?? '').trim();

/**
 * Parse a required whole number. Returns null when absent or malformed, so the
 * caller reports a field error rather than sending NaN.
 *
 * parseInt('') is NaN, and JSON.stringify(NaN) is `null`, so the previous code
 * silently posted {"sets": null} when a numeric field was cleared.
 */
function parseRequiredInt(raw) {
  const text = asText(raw);
  if (text === '') return null;
  if (!/^-?\d+$/.test(text)) return null;
  return Number(text);
}

/** Parse an optional number. Returns undefined when blank, null when malformed. */
function parseOptionalNumber(raw) {
  const text = asText(raw);
  if (text === '') return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

const inRange = (value, { min, max }) => value >= min && value <= max;

/**
 * Validate the form. Returns an object keyed by field name; empty when valid.
 *
 * Every violation is reported at once, matching the backend's FieldErrors
 * behaviour, so a coach is not told about one problem per attempt.
 */
export function validatePrescriptionForm(form = {}, loadLimits = DEFAULT_LOAD_LIMITS) {
  const errors = {};

  const series = asText(form.series);
  if (series === '') {
    errors.series = 'Series is required.';
  } else if (!SERIES_PATTERN.test(series)) {
    errors.series =
      'Series must be a capital letter with up to two optional digits, e.g. "A" or "A1".';
  }

  const sets = parseRequiredInt(form.sets);
  if (sets === null) {
    errors.sets = 'Sets must be a whole number.';
  } else if (!inRange(sets, LIMITS.sets)) {
    errors.sets = `Sets must be between ${LIMITS.sets.min} and ${LIMITS.sets.max}.`;
  }

  // Reps is a scheme, not a number: a count, a range, a wave with one value per
  // set, or a duration or distance. See repScheme.js — four of those five could not
  // be expressed while this was parseRequiredInt.
  const repsError = validateRepScheme(form.reps, form.sets, LIMITS.reps);
  if (repsError) {
    errors.reps = repsError;
  }

  // Tempo is optional; when present it must be well formed.
  const tempo = asText(form.tempo);
  if (tempo !== '' && !TEMPO_PATTERN.test(tempo)) {
    errors.tempo =
      'Tempo must be four phases separated by hyphens, each a number of seconds or "X", e.g. "3-1-1-0".';
  }

  const rest = parseRequiredInt(form.rest);
  if (rest === null) {
    errors.rest = 'Rest must be a whole number of seconds.';
  } else if (!inRange(rest, LIMITS.rest)) {
    errors.rest = `Rest must be between ${LIMITS.rest.min} and ${LIMITS.rest.max} seconds.`;
  }

  // Intensity — all optional, range-checked when present.
  const intensity = [
    ['load', 'Load', loadLimits],
    ['percentOneRM', '% of 1RM', LIMITS.percentOneRM],
    ['rpe', 'RPE', LIMITS.rpe],
    ['rir', 'RIR', LIMITS.rir],
  ];

  for (const [field, label, limits] of intensity) {
    const value = parseOptionalNumber(form[field]);
    if (value === undefined) continue;
    if (value === null) {
      errors[field] = `${label} must be a number.`;
    } else if (!inRange(value, limits)) {
      errors[field] = `${label} must be between ${limits.min} and ${limits.max}.`;
    }
  }

  // RPE must land on a half point: the scale is not meaningful at a precision
  // the athlete cannot report.
  const rpe = parseOptionalNumber(form.rpe);
  if (typeof rpe === 'number' && !errors.rpe && (rpe * 2) % 1 !== 0) {
    errors.rpe = 'RPE must be a whole or half number, e.g. 7 or 7.5.';
  }

  // RIR must be a whole number of reps.
  const rir = parseOptionalNumber(form.rir);
  if (typeof rir === 'number' && !errors.rir && !Number.isInteger(rir)) {
    errors.rir = 'RIR must be a whole number.';
  }

  // RPE and RIR restate the same quantity (RIR ≈ 10 − RPE), so accepting both
  // admits a self-contradicting prescription. The server rejects this too, but
  // its message names only "rpe", which reads oddly beside a filled RIR field.
  if (typeof rpe === 'number' && typeof rir === 'number' && !errors.rpe && !errors.rir) {
    errors.rpe = 'Use either RPE or RIR, not both — they express the same thing.';
  }

  return errors;
}

/**
 * Build the POST body for adding an exercise to a session.
 *
 * @returns {{body: object}|{errors: object}} `errors` keyed by field on failure.
 */
export function buildExerciseRequest(form, selectedExercise, unit, loadLimits = DEFAULT_LOAD_LIMITS) {
  if (!selectedExercise?.name) {
    return { errors: { exercise: 'Choose an exercise first.' } };
  }

  const errors = validatePrescriptionForm(form, loadLimits);
  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  const body = {
    exerciseName: selectedExercise.name,
    series: asText(form.series),
    sets: parseRequiredInt(form.sets),
    repScheme: asText(form.reps),
    tempo: asText(form.tempo),
    rest: parseRequiredInt(form.rest),
    notes: asText(form.notes),
  };

  // Omit blank intensity fields entirely rather than sending null, so the
  // backend's nullable columns stay null and PRESC-API-3 (a prescription with no
  // intensity is valid) holds.
  for (const field of ['load', 'percentOneRM', 'rpe', 'rir']) {
    const value = parseOptionalNumber(form[field]);
    if (typeof value === 'number') {
      body[field] = value;
    }
  }

  // State the unit the load was typed in. The server converts to kilograms
  // before validating, so it must know — omitting it would fall back to the
  // caller's stored preference, which is usually the same but need not be.
  if (typeof body.load === 'number' && unit) {
    body.unit = unit;
  }

  return { body };
}

/**
 * The request body for replacing an existing prescription.
 *
 * The same fields as buildExerciseRequest minus `exerciseName`: PUT cannot change
 * which exercise a prescription is for. That is a different prescription, and the
 * sets already logged against the old one would silently reattach to the new
 * movement — so the backend does not accept it and neither does this.
 *
 * Blank intensity fields are still omitted rather than sent as null, and that now
 * carries weight it did not before: PUT is a full replace, so an omitted `rpe`
 * *clears* a stored one. Under the create path an omission simply meant "none".
 */
export function buildExerciseUpdateRequest(form, unit, loadLimits = DEFAULT_LOAD_LIMITS) {
  const errors = validatePrescriptionForm(form, loadLimits);
  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  const body = {
    series: asText(form.series),
    sets: parseRequiredInt(form.sets),
    repScheme: asText(form.reps),
    tempo: asText(form.tempo),
    rest: parseRequiredInt(form.rest),
    notes: asText(form.notes),
  };

  for (const field of ['load', 'percentOneRM', 'rpe', 'rir']) {
    const value = parseOptionalNumber(form[field]);
    if (typeof value === 'number') {
      body[field] = value;
    }
  }

  if (typeof body.load === 'number' && unit) {
    body.unit = unit;
  }

  return { body };
}

/**
 * Fill the prescription form from a stored entry, so editing starts from what is
 * actually prescribed rather than from blanks.
 *
 * Reads `load`, never `loadKg`. The API sends both — `load` in the reader's unit,
 * `loadKg` canonical — and prefilling from the canonical value would show a pound
 * coach their prescription in kilograms and then submit that number back as pounds.
 *
 * Every value becomes a string because these feed controlled text inputs; a numeric
 * 0 would render as "0" either way, but `undefined` would make the input
 * uncontrolled and React would warn on the first keystroke.
 */
export function formFromEntry(entry) {
  const text = (value) => (value === null || value === undefined ? '' : String(value));

  return {
    series: text(entry?.series),
    sets: text(entry?.sets),
    // The API sends both; repScheme is authoritative and reps is the legacy
    // integer kept for clients that predate it.
    reps: text(entry?.repScheme || entry?.reps),
    tempo: text(entry?.tempo),
    rest: text(entry?.rest),
    notes: text(entry?.notes),
    load: text(entry?.load),
    percentOneRM: text(entry?.percentOneRM),
    rpe: text(entry?.rpe),
    rir: text(entry?.rir),
  };
}

/**
 * Extract a human-readable message from an axios error.
 *
 * The backend answers `{error, details}` — details carries the field-level
 * validation text — so prefer details, then error, then the transport message.
 * The previous code discarded all of it into console.error.
 */
export function apiErrorMessage(error, fallback = 'Something went wrong.') {
  const data = error?.response?.data;
  if (data) {
    if (data.details && data.error) return `${data.error}: ${data.details}`;
    if (data.details) return data.details;
    if (data.error) return data.error;
  }
  if (error?.response?.status === 404) return 'Not found.';
  return error?.message || fallback;
}
